/**
 * Convex v2 validation schemas using v.function
 * These schemas are used for runtime validation of data
 */

import { v } from "convex/values";

/**
 * File type validation
 */
const fileType = v.union(
  v.literal("pdf"),
  v.literal("txt"),
  v.literal("xlsx"),
  v.literal("ods"),
  v.literal("png"),
  v.literal("jpg"),
  v.literal("jpeg"),
  v.literal("docx"),
  v.literal("pptx"),
);

/**
 * Document status validation
 */
const documentStatus = v.union(
  v.literal("uploading"),
  v.literal("processing"),
  v.literal("ready"),
  v.literal("error"),
);

/**
 * Audit status validation
 */
const auditStatus = v.union(
  v.literal("draft"),
  v.literal("in_progress"),
  v.literal("completed"),
  v.literal("archived"),
);

/**
 * Audit trigger validation
 */
const auditTrigger = v.union(
  v.literal("manual"),
  v.literal("scheduled"),
  v.literal("event_based"),
);

/**
 * Checklist item status validation
 */
const checklistItemStatus = v.union(
  v.literal("not_started"),
  v.literal("in_progress"),
  v.literal("completed"),
  v.literal("skipped"),
);

/**
 * Document validation schema
 */
export const documentSchema = v.object({
  fileName: v.string(),
  fileType,
  status: v.optional(documentStatus),
});

/**
 * Document upload input schema
 */
export const documentUploadInputSchema = v.object({
  fileName: v.string(),
  fileType,
  storageId: v.id("_storage"),
  contentHash: v.optional(v.string()),
});

/**
 * Document metadata update schema
 */
export const documentMetadataUpdateSchema = v.object({
  fileName: v.optional(v.string()),
  summary: v.optional(v.string()),
});

/**
 * Audit validation schema
 */
export const auditSchema = v.object({
  code: v.string(),
  name: v.string(),
  description: v.optional(v.string()),
  status: v.optional(auditStatus),
  deadline: v.optional(v.number()),
  trigger: auditTrigger,
});

/**
 * Audit creation input schema
 */
export const auditCreateInputSchema = v.object({
  code: v.string(),
  name: v.string(),
  description: v.optional(v.string()),
  deadline: v.optional(v.number()),
  trigger: auditTrigger,
  templateId: v.optional(v.string()),
  tagIds: v.optional(v.array(v.string())),
});

/**
 * Audit update input schema
 */
export const auditUpdateInputSchema = v.object({
  code: v.optional(v.string()),
  name: v.optional(v.string()),
  description: v.optional(v.string()),
  status: v.optional(auditStatus),
  deadline: v.optional(v.number()),
  notes: v.optional(v.string()),
  tagIds: v.optional(v.array(v.string())),
});

/**
 * Checklist item validation schema
 */
export const checklistItemSchema = v.object({
  clause: v.string(),
  description: v.optional(v.string()),
  status: v.optional(checklistItemStatus),
  notes: v.optional(v.string()),
});

/**
 * Checklist item creation input schema
 */
export const checklistItemCreateInputSchema = v.object({
  auditId: v.id("audits"),
  clause: v.string(),
  description: v.optional(v.string()),
  status: v.optional(checklistItemStatus),
});

/**
 * Checklist item update input schema
 */
export const checklistItemUpdateInputSchema = v.object({
  clause: v.optional(v.string()),
  description: v.optional(v.string()),
  status: v.optional(checklistItemStatus),
  notes: v.optional(v.string()),
});

/**
 * Document filters schema
 */
export const documentFiltersSchema = v.object({
  search: v.optional(v.string()),
  tagIds: v.optional(v.array(v.string())),
  status: v.optional(documentStatus),
  uploadedBy: v.optional(v.string()),
  auditId: v.optional(v.string()),
});

/**
 * Audit filters schema
 */
export const auditFiltersSchema = v.object({
  search: v.optional(v.string()),
  status: v.optional(v.array(auditStatus)),
  tagIds: v.optional(v.array(v.string())),
  createdBy: v.optional(v.string()),
  deadlineBefore: v.optional(v.number()),
  deadlineAfter: v.optional(v.number()),
});
